import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout';
import { Toast } from '../../components';
import { AsyncSearchSelect, AsyncSearchSelectHandle } from '../../components/forms';
import { carService, voyageDropdownService, VoyageDropdownItem } from '../../services';
import { useToast } from '../../hooks/useToast';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { Car } from '../../types';
import { formatDate } from '../../utils/tableUtils';

const ChangeVoyageChassisPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();

  const [chassisNumber, setChassisNumber] = useState('');
  const [car, setCar] = useState<Car | null>(null);
  const [loadingCarDetails, setLoadingCarDetails] = useState(false);
  const [searchResults, setSearchResults] = useState<Car[]>([]);
  const [showCarDropdown, setShowCarDropdown] = useState(false);
  const [selectedToVoyage, setSelectedToVoyage] = useState<VoyageDropdownItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [highlightedCarIndex, setHighlightedCarIndex] = useState(-1);
  const carDropdownRef = useRef<HTMLDivElement>(null);
  const chassisInputRef = useRef<HTMLInputElement>(null);
  const updateButtonRef = useRef<HTMLButtonElement>(null);
  const toVoyageRef = useRef<AsyncSearchSelectHandle>(null);

  // Debounce chassis number for search
  const debouncedChassisNumber = useDebouncedValue(chassisNumber, 300);

  // Load voyages from backend
  const loadVoyages = useCallback(async (query: string): Promise<VoyageDropdownItem[]> => {
    try {
      return await voyageDropdownService.getAll({ query, limit: 50 });
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Voyage search error:', error);
      }
      return [];
    }
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (carDropdownRef.current && !carDropdownRef.current.contains(event.target as Node)) {
        setShowCarDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const SEARCH_LIMIT = 20;

  // Search for cars as user types (debounced)
  useEffect(() => {
    const searchCars = async () => {
      if (!debouncedChassisNumber.trim() || debouncedChassisNumber.length < 2) {
        setSearchResults([]);
        setShowCarDropdown(false);
        setHasMore(false);
        return;
      }

      try {
        // Use search endpoint with 'chs_no' column to search by short chassis number
        const cars = await carService.search(debouncedChassisNumber.trim(), 'chs_no', {
          limit: SEARCH_LIMIT
        });
        setSearchResults(cars);
        setShowCarDropdown(cars.length > 0);
        setHasMore(cars.length === SEARCH_LIMIT);
      } catch (error: any) {
        console.error('Error searching for cars:', error);
        setSearchResults([]);
        setShowCarDropdown(false);
        setHasMore(false);
      }
    };

    searchCars();
  }, [debouncedChassisNumber]);

  // Highlight first item when search results change
  useEffect(() => {
    setHighlightedCarIndex(searchResults.length > 0 ? 0 : -1);
  }, [searchResults]);

  // Load more results
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const cars = await carService.search(debouncedChassisNumber.trim(), 'chs_no', {
        skip: searchResults.length,
        limit: SEARCH_LIMIT
      });
      setSearchResults(prev => [...prev, ...cars]);
      setHasMore(cars.length === SEARCH_LIMIT);
    } catch (error: any) {
      console.error('Error loading more cars:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle car selection from dropdown
  const handleCarSelect = useCallback(async (selectedCar: Car) => {
    setChassisNumber(selectedCar.chassis_number);
    setShowCarDropdown(false);
    setLoadingCarDetails(true);

    // Always fetch full car with relations to ensure voyage data is loaded
    try {
      const foundCar = await carService.getById(selectedCar.id);
      // Debug: log voyage data to help diagnose issues
      if (foundCar.voyage_id && !foundCar.voyage) {
        console.warn('Car has voyage_id but voyage relation not loaded:', foundCar.voyage_id);
      }
      setCar(foundCar);
      // Focus on "To Voyage" field after selection
      setTimeout(() => toVoyageRef.current?.focus(), 100);
    } catch (error: any) {
      console.error('Error fetching car details:', error);
      showToast('Failed to load car details', 'error');
      // Fallback to the selected car even if fetch fails
      setCar(selectedCar);
    } finally {
      setLoadingCarDetails(false);
    }
  }, [showToast]);

  // Handle voyage selection
  const handleVoyageSelect = (voyage: VoyageDropdownItem) => {
    setSelectedToVoyage(voyage);
    // Focus on Update Voyage button after selection
    setTimeout(() => {
      updateButtonRef.current?.focus();
    }, 100);
  };

  // Submit the change
  const handleSubmit = async () => {
    if (!car) {
      showToast('Please search for a car first', 'error');
      return;
    }

    if (car.loaded) {
      showToast('Cannot change voyage - vehicle is already loaded', 'error');
      return;
    }

    if (!selectedToVoyage) {
      showToast('Please select a destination voyage', 'error');
      return;
    }

    if (car.voyage_id === selectedToVoyage.id) {
      showToast('Car is already assigned to this voyage', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await carService.bulkUpdateVoyage([car.id], selectedToVoyage.id);
      showToast('Voyage updated successfully', 'success');
      
      // Clear chassis and car, keep voyage selection, return focus to chassis input
      setChassisNumber('');
      setCar(null);
      setSearchResults([]);
      setTimeout(() => {
        chassisInputRef.current?.focus();
      }, 100);
    } catch (error: any) {
      console.error('Error updating voyage:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to update voyage';
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Load available voyages (filters out current voyage)
  const loadAvailableVoyages = useCallback(async (query: string): Promise<VoyageDropdownItem[]> => {
    const voyages = await loadVoyages(query);
    if (!car || !car.voyage_id) {
      return voyages;
    }
    return voyages.filter(voyage => voyage.id !== car.voyage_id);
  }, [loadVoyages, car]);

  // Get car name display (make + model)
  const getCarName = (car: Car | null) => {
    if (!car) return '';
    const make = car.make?.name || '';
    const model = car.model?.name || '';
    return [make, model].filter(Boolean).join(' ').trim() || car.name;
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Change Voyage Chassis Number
          </h1>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {/* Chassis Number Search */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Chassis Number:
            </label>
            <div className="flex gap-4">
              <div className="flex-1 relative" ref={carDropdownRef}>
                <input
                  ref={chassisInputRef}
                  type="text"
                  value={chassisNumber}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setChassisNumber(newValue);
                    setShowCarDropdown(true);
                    if (!newValue) {
                      setCar(null);
                      setSearchResults([]);
                    } else if (car && car.chassis_number !== newValue) {
                      // Clear car if chassis number doesn't match
                      setCar(null);
                    }
                  }}
                  onFocus={() => {
                    if (searchResults.length > 0) {
                      setShowCarDropdown(true);
                    }
                  }}
                  onKeyDown={(e) => {
                    // Shift+Enter always triggers submit
                    if (e.key === 'Enter' && e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                      return;
                    }
                    if (showCarDropdown && searchResults.length > 0) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setHighlightedCarIndex(prev => 
                          prev < searchResults.length - 1 ? prev + 1 : 0
                        );
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setHighlightedCarIndex(prev => 
                          prev > 0 ? prev - 1 : searchResults.length - 1
                        );
                      } else if (e.key === 'Enter' && highlightedCarIndex >= 0) {
                        e.preventDefault();
                        handleCarSelect(searchResults[highlightedCarIndex]);
                      } else if (e.key === 'Escape') {
                        setShowCarDropdown(false);
                        setHighlightedCarIndex(-1);
                      }
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      if (car && selectedToVoyage && !car.loaded) {
                        handleSubmit();
                      }
                    }
                  }}
                  placeholder="Enter chassis number..."
                  className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
                {showCarDropdown && searchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map((result, index) => (
                      <div
                        key={result.id}
                        onClick={() => handleCarSelect(result)}
                        className={`px-4 py-2 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0 ${
                          index === highlightedCarIndex
                            ? 'bg-blue-100 dark:bg-gray-500'
                            : 'hover:bg-blue-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {result.chassis_number}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {getCarName(result) || result.name}
                        </div>
                      </div>
                    ))}
                    {hasMore && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadMore();
                        }}
                        disabled={loadingMore}
                        className="w-full px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-600 font-medium"
                      >
                        {loadingMore ? 'Loading...' : 'Load More'}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <input
                type="text"
                value={getCarName(car)}
                readOnly
                placeholder="Car name will appear here..."
                className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 dark:text-gray-400 text-gray-600"
              />
            </div>
          </div>

          {loadingCarDetails && (
            <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
              Loading car details...
            </div>
          )}

          {/* Loaded vehicle warning */}
          {car && car.loaded && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-700 dark:text-red-400">
                ⚠️ This vehicle is already loaded and cannot be moved to another voyage.
              </p>
            </div>
          )}

          <div className="space-y-6 border-t border-gray-200 dark:border-gray-700 pt-6">
            {/* From Voyage Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                From Voyage
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    From Voyage:
                  </label>
                  <input
                    type="text"
                    value={car?.voyage?.voyage_code || '-'}
                    readOnly
                    className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 dark:text-gray-400 text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Vessel:
                  </label>
                  <input
                    type="text"
                    value={car?.voyage?.vessel_description || '-'}
                    readOnly
                    className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 dark:text-gray-400 text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date of issue:
                  </label>
                  <input
                    type="text"
                    value={formatDate(car?.voyage?.departure, '-')}
                    readOnly
                    className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 dark:text-gray-400 text-gray-600"
                  />
                </div>
              </div>
            </div>

            {/* To Voyage Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                To Voyage
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    To Voyage:
                  </label>
                  <AsyncSearchSelect<VoyageDropdownItem>
                    ref={toVoyageRef}
                    loadOptions={loadAvailableVoyages}
                    getValue={(voyage) => voyage.id}
                    getLabel={(voyage) => voyage.voyage_code}
                    getSecondaryLabel={(voyage) => voyage.vessel_description}
                    onSelect={handleVoyageSelect}
                    value={selectedToVoyage?.id || ''}
                    initialDisplayValue={selectedToVoyage?.voyage_code || ''}
                    placeholder="Search voyage..."
                    noOptionsMessage="No voyages found"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Vessel:
                  </label>
                  <input
                    type="text"
                    value={selectedToVoyage?.vessel_description || '-'}
                    readOnly
                    className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 dark:text-gray-400 text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date of issue:
                  </label>
                  <input
                    type="text"
                    value={formatDate(selectedToVoyage?.departure, '-')}
                    readOnly
                    className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 dark:text-gray-400 text-gray-600"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end mt-6">
              <button
                ref={updateButtonRef}
                onClick={handleSubmit}
                disabled={submitting || !selectedToVoyage || !car || car.loaded}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Updating...' : 'Update Voyage'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={hideToast}
        />
      )}
    </Layout>
  );
};

export default ChangeVoyageChassisPage;

